/**
 * `/products/:itemNo/edit` — the product edit form (build item 13,
 * docs/plan.md → Screens, "Product add / edit"). hideShellChrome per the
 * design's `isProductEdit` branch.
 *
 * Resolves the product to edit from the offline-first Dexie catalogue mirror
 * (`useProductByItemNo`), same as the detail route. `ProductFormView` seeds
 * its form fields from `product` once, on mount — so this route waits for the
 * mirror to actually resolve the row before mounting the form at all, rather
 * than mounting it early against a still-`undefined` product and losing the
 * prefill.
 */
import { createFileRoute, Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ProductFormView } from '@/components/product-mgmt/product-form-view';
import { useProductByItemNo } from '@/components/product-mgmt/use-product-by-item-no';
import { useIsOffline } from '@/lib/offline';

export const Route = createFileRoute('/_app/products/$itemNo/edit')({
  staticData: { hideShellChrome: true },
  component: ProductEditRoute
});

function ProductEditRoute() {
  const { itemNo } = Route.useParams();
  const numericItemNo = Number(itemNo);
  const { product, loading } = useProductByItemNo(numericItemNo);
  const isOffline = useIsOffline();

  // Hold a spinner while the mirror is still resolving — after a full reload
  // (e.g. the OS reclaimed the renderer during camera capture) this window is
  // exactly when the old code flashed "not found" and bounced to /products.
  if (loading) {
    return (
      <div className="grid h-dvh place-items-center p-6">
        <Spinner className="size-5" aria-hidden="true" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="grid h-dvh place-items-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-sm text-muted-foreground">
            This product couldn't be found{isOffline ? ' — it may not have synced to this device yet.' : '.'}
          </p>
          <Button render={<Link to="/products" />} variant="outline">
            ‹ Back to Products
          </Button>
        </div>
      </div>
    );
  }

  return <ProductFormView mode="edit" product={product} />;
}
